import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { loadModules } from 'esri-loader';
import { useEffect, useRef, useState } from 'react';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';
import '../template.module.css';

export default function LiveMap({
  webmapID,
  token,
  selectedBlock,
  defaultSettings = true,
}) {
  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [uniqueValueInfos, setUniqueValueInfos] = useState([]);
  const [isCategorical, setIsCategorical] = useState(false);
  const pageSize = 1000;

  const isMounted = useRef(true);

  console.log({
    selectedBlock,
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let view;
    let webmap = null;
    let customLayer = null;
    let map = null;
    let legend = null;
    let graphicsToRemove = [];

    const initializeMap = async () => {
      try {
        const [
          Map,
          MapView,
          FeatureLayer,
          Legend,
          esriConfig,
          classBreaks,
          WebMap,
          Extent,
          Graphic,
        ] = await loadModules([
          'esri/Map',
          'esri/views/MapView',
          'esri/layers/FeatureLayer',
          'esri/widgets/Legend',
          'esri/config',
          'esri/smartMapping/statistics/classBreaks',
          'esri/WebMap',
          'esri/geometry/Extent',
          'esri/Graphic',
        ]);

        if (!webmapID || !token) return;
        esriConfig.apiKey = token;

        const extent = new Extent({
          xmin: -94.590938015952,
          ymin: 39.062328001212,
          xmax: -94.588938015952,
          ymax: 39.064328001212,
          spatialReference: {
            wkid: 4326,
          },
        });

        if (defaultSettings) {
          webmap = new WebMap({
            portalItem: {
              id: webmapID,
            },
          });

          view = new MapView({
            container: mapRef.current,
            map: webmap,
            extent: extent,
            zoom: 14,
            navigation: {
              mouseWheelZoomEnabled: false,
            },
          });

          view.when(() => {
            if (isMounted.current) {
              setIsMapLoaded(true);
            }
          });

          legend = new Legend({
            view: view,
          });
          view.ui.add(legend, 'top-right');

          const customSymbol = {
            type: 'picture-marker',
            url: marker,
            width: '50px',
            height: '100px',
          };
          const center = {
            longitude: (extent.xmin + extent.xmax) / 2,
            latitude: (extent.ymin + extent.ymax) / 2,
          };

          const graphic = new Graphic({
            geometry: {
              type: 'point',
              longitude: center.longitude,
              latitude: center.latitude,
            },
            symbol: customSymbol,
          });
          view.graphics.add(graphic);
          graphicsToRemove.push(graphic);
        } else {
          // Extract the actual block from nested structure if it exists
          const actualBlock =
            selectedBlock?.customMapConfig?.selectedBlock || selectedBlock;

          // Validate that selectedBlock and serviceURL exist
          if (!actualBlock || !actualBlock.serviceURL) {
            console.error(
              'Selected block or service URL is missing. Cannot create feature layer.',
              { selectedBlock, actualBlock },
            );
            return;
          }

          const defaultSym = {
            type: 'simple-fill',
            outline: {
              color: actualBlock?.boundaries
                ? [57, 88, 92, 1]
                : [255, 255, 255, 0],
              width: '1px',
            },
          };

          const renderer = {
            type: 'simple',
            symbol: defaultSym,
            label: `U.S. ${actualBlock.group}`,
            visualVariables: [
              {
                type: 'color',
                field: actualBlock.outfields[0],
                legendOptions: {},
              },
            ],
          };

          customLayer = new FeatureLayer({
            url: actualBlock.serviceURL,
            renderer: renderer,
            title: actualBlock.title + ` (${actualBlock.group})`,
            popupTemplate: {
              title: actualBlock.title + ` (${actualBlock.group})`,
              content: `{${actualBlock.outfields[0]}}`,
              fieldInfos: [
                {
                  fieldName: actualBlock.outfields[0],
                  format: {
                    digitSeparator: true,
                    places: 0,
                  },
                },
              ],
            },
          });

          customLayer.opacity = 0.6;
          customLayer.minScale = 0;
          customLayer.maxScale = 0;

          console.log({
            outfield: actualBlock.outfields[0],
          });

          const checkFieldType = async (layer, fieldName) => {
            await layer.load();
            const fields = layer.fields;
            const field = fields.find(f => f.name === fieldName);
            return [
              'integer',
              'small-integer',
              'big-integer',
              'single',
              'double',
            ].includes(field.type);
          };

          try {
            // Check if the field is numeric
            const isNumeric = await checkFieldType(
              customLayer,
              actualBlock.outfields[0],
            );
            let response;

            if (isNumeric) {
              // Use existing class breaks for numeric fields
              response = await classBreaks({
                layer: customLayer,
                field: actualBlock.outfields[0],
                classificationMethod: 'quantile',
              });
            } else {
              if (isMounted.current) {
                setIsCategorical(true);
              }

              // For non-numeric fields, get unique values with pagination
              const loadCategoricalData = async (offset = 0) => {
                const featureResponse = await customLayer.queryFeatures({
                  outFields: [actualBlock.outfields[0]],
                  returnDistinctValues: true,
                  orderByFields: [actualBlock.outfields[0]],
                  where: '1=1',
                  returnGeometry: false,
                  f: 'json',
                  num: 3500,
                  start: offset,
                });

                const uniqueValues = featureResponse.features
                  .map(feature => feature.attributes[actualBlock.outfields[0]])
                  .filter(value => value !== null && value !== undefined);

                // Create unique value renderer
                const valueInfos = uniqueValues.map((value, index) => ({
                  value: value,
                  symbol: {
                    type: 'simple-fill',
                    color: [
                      '#a8e9c7',
                      '#d9f0ae',
                      '#f9d58d',
                      '#f1a178',
                      '#e66565',
                    ][index % 5], // Cycle through colors
                    outline: {
                      color: actualBlock?.boundaries
                        ? [57, 88, 92, 1]
                        : [255, 255, 255, 0],
                      width: '1px',
                    },
                  },
                  label: value.toString(),
                }));

                if (isMounted.current) {
                  setUniqueValueInfos(valueInfos);
                }

                return {
                  uniqueValueInfos: valueInfos,
                  isUniqueValue: true,
                  isEmpty: uniqueValues.length === 0,
                };
              };

              response = await loadCategoricalData(offset);
            }

            // Apply the appropriate renderer based on response
            if (response.isUniqueValue) {
              // Apply unique value renderer
              customLayer.renderer = {
                type: 'unique-value',
                field: actualBlock.outfields[0],
                uniqueValueInfos: response.uniqueValueInfos,
              };
            } else {
              // Apply class breaks renderer
              let breakInfos = response.classBreakInfos;
              const dynamicStops = breakInfos.map((info, index) => ({
                value: info.maxValue,
                color:
                  index === 0
                    ? '#a8e9c7'
                    : index === 1
                    ? '#d9f0ae'
                    : index === 2
                    ? '#f9d58d'
                    : index === 3
                    ? '#f1a178'
                    : '#e66565',
                label: info.label,
              }));

              customLayer.renderer = {
                type: 'simple',
                symbol: defaultSym,
                visualVariables: [
                  {
                    type: 'color',
                    field: actualBlock.outfields[0],
                    stops: dynamicStops,
                  },
                ],
              };
            }

            // Create the map with the customized layer
            map = new Map({
              basemap: 'gray-vector',
              layers: [customLayer],
            });

            view = new MapView({
              container: mapRef.current,
              map: map,
              extent: extent,
              navigation: {
                mouseWheelZoomEnabled: false,
              },
              scale:
                customLayer.minScale === 0
                  ? customLayer.maxScale
                  : customLayer.minScale,
            });

            view.when(() => {
              if (isMounted.current) {
                setIsMapLoaded(true);
              }
            });

            legend = new Legend({
              view: view,
            });
            view.ui.add(legend, 'top-right');

            const customSymbol = {
              type: 'picture-marker',
              url: marker,
              width: '50px',
              height: '100px',
            };
            const center = {
              longitude: (extent.xmin + extent.xmax) / 2,
              latitude: (extent.ymin + extent.ymax) / 2,
            };

            const graphic = new Graphic({
              geometry: {
                type: 'point',
                longitude: center.longitude,
                latitude: center.latitude,
              },
              symbol: customSymbol,
            });
            view.graphics.add(graphic);
            graphicsToRemove.push(graphic);
          } catch (error) {
            console.error('Error in renderer creation:', error);
            if (isMounted.current) {
              alert('Error creating map renderer.');
            }
          }
        }
      } catch (error) {
        console.error('Error loading modules:', error);
      }
    };

    initializeMap();

    return () => {
      isMounted.current = false;

      if (view) {
        if (graphicsToRemove.length > 0) {
          graphicsToRemove.forEach(graphic => {
            if (view.graphics) {
              view.graphics.remove(graphic);
            }
          });
        }

        if (legend && view.ui) {
          view.ui.remove(legend);
        }

        view.container = null;
        view.destroy();
      }

      if (map) {
        if (customLayer) {
          map.remove(customLayer);
        }
        map.destroy && map.destroy();
      }

      if (webmap && webmap.destroy) {
        webmap.destroy();
      }

      if (customLayer && customLayer.destroy) {
        customLayer.destroy();
      }

      view = null;
      map = null;
      webmap = null;
      customLayer = null;
      legend = null;
      graphicsToRemove = [];
    };
  }, [webmapID, token, selectedBlock, defaultSettings, currentPage, offset]);

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
    setOffset(prev => prev + 3500);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
    setOffset(prev => Math.max(0, prev - 3500));
  };

  return (
    <Box sx={{ position: 'relative', height: '30rem', width: '100%' }}>
      {!isMapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          <CircularProgress
            sx={{
              color: '#4FA490',
            }}
          />
        </Box>
      )}
      <div
        ref={mapRef}
        style={{
          height: '30rem',
          width: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      ></div>

      {isCategorical && isMapLoaded && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            marginTop: '0.5rem',
            position: 'absolute',
            bottom: '10px',
            left: 0,
            right: 0,
          }}
        >
          <Button
            variant='contained'
            disabled={currentPage === 0}
            onClick={handlePrevPage}
            sx={{
              backgroundColor: '#4FA490',
              '&:hover': {
                backgroundColor: '#39585C',
              },
              '&.Mui-disabled': {
                backgroundColor: '#cccccc',
              },
            }}
          >
            Previous Page
          </Button>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold',
            }}
          >
            Page {currentPage + 1} ({uniqueValueInfos.length} items)
          </Typography>
          <Button
            variant='contained'
            onClick={handleNextPage}
            disabled={uniqueValueInfos.length < pageSize}
            sx={{
              backgroundColor: '#4FA490',
              '&:hover': {
                backgroundColor: '#39585C',
              },
              '&.Mui-disabled': {
                backgroundColor: '#cccccc',
              },
            }}
          >
            Next Page
          </Button>
        </Box>
      )}
    </Box>
  );
}
